import _, {isString} from 'lodash';
import React, {FC, useEffect, useRef} from 'react';
import {useDrop} from 'react-dnd';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {swap} from '../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {FrameTypesEnum} from '../../../../../../libs/shared/core/src/models/api/apiTypes';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {SidePanelMultiViewDetailsModel} from '../../../../../models/api/localPreferencesModel';
import {SidePanelFrame} from '../../../../../store/stateless/sidePanelFrameSelectors';
import {SwitcherDragItemType} from '../draggableSwitcherItem';
import {MultiViewButtonDropArea} from './multiViewButtonDropArea';

/*
 * Constants.
 */

const multiViewDropIds = ['multi-view-drop-area-1', 'multi-view-drop-area-2'];

/*
 * Props.
 */

interface MultiViewButtonProps {
  panelId: ViewerPanelId;
  visibleFrames: ReadonlyArray<SidePanelFrame>;
  multiViewDetails: SidePanelMultiViewDetailsModel;
  isMultiViewSelected: boolean;
  frameIdOverMultiViewButton: string | undefined;
  setFrameIdOverMultiViewButton: (arg: string | undefined) => void;
  updateSelectedSideFrameId: (newSelectedFrameId: string | undefined, panelId: ViewerPanelId) => void;
  updateSidepanelMultiViewSelectedFrameIds: (arg: ReadonlyArray<string | null>) => void;
  onFrameDragOver: (frameId: string, overFrameId: string) => void;
  onFrameDragStart: (arg: string) => void;
  onFrameDragEnd: () => void;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  justify-content: center;
`;

interface StyledDivProps {
  $isOver: boolean;
  $isOpen: boolean;
}

const StyledMultiViewButtonWrapperDiv = styled.div<StyledDivProps>`
  display: flex;
  flex-flow: column;
  padding: 4px;
  border-radius: 32px;
  margin: -4px -4px 0 -4px;
  gap: 8px;
  width: 38px;

  ${(p) => addButtonStyles(p.$isOpen)}
  ${(p) =>
    p.$isOver &&
    css`
      background: ${p.theme.palette.blue.shade20};
    `};
`;

function addButtonStyles(isOpen: boolean) {
  if (isOpen) {
    return css`
      background: ${(p) => p.theme.foreground};
      ${(p) => boxShadow(p.theme, `0px 2px 9px ${p.theme.alphas.black50}`)};
    `;
  }
  return css`
    background: ${(p) => p.theme.greys.shade20};

    &:hover {
      background: ${(p) => p.theme.greys.shade30};
    }
  `;
}

/*
 * Component.
 */

export const MultiViewButton: FC<MultiViewButtonProps> = (props) => {
  const {
    isMultiViewSelected,
    multiViewDetails,
    visibleFrames,
    frameIdOverMultiViewButton,
    setFrameIdOverMultiViewButton,
    updateSelectedSideFrameId,
    updateSidepanelMultiViewSelectedFrameIds,
    onFrameDragOver,
    onFrameDragStart,
    onFrameDragEnd,
  } = props;

  const wasDroppedOnAreaRef = useRef<boolean>(false);

  const [{isOver}, dropRef] = useDrop({
    accept: SwitcherDragItemType,
    collect: (monitor) => ({isOver: monitor.isOver()}),
    hover: (item: any) => {
      const itemFrameId = item && item.frameId;

      if (!isString(itemFrameId) || frameIdOverMultiViewButton === itemFrameId) {
        return;
      }
      setFrameIdOverMultiViewButton(itemFrameId);
    },
  });

  const previousIsOver = usePrevious(isOver);

  useEffect(() => {
    // If we are currently over, the frame is not over button or we just dropped on a sub area of the button, do nothing.
    if (isOver || isOver === previousIsOver || !frameIdOverMultiViewButton || wasDroppedOnAreaRef.current) {
      return;
    }
    setFrameIdOverMultiViewButton(undefined);
  }, [frameIdOverMultiViewButton, isOver, previousIsOver, setFrameIdOverMultiViewButton]);

  const onButtonClick = () => {
    updateSelectedSideFrameId(isMultiViewSelected ? undefined : FrameTypesEnum.MULTI_VIEW, props.panelId);
  };

  return (
    <StyledWrapperDiv>
      <TooltipCoordinator
        customMargin={4}
        render={() => <Trans id="multi-view-button-tooltip">Multi-view</Trans>}
        placement="bottom-end"
      >
        <StyledMultiViewButtonWrapperDiv
          ref={dropRef}
          className={buildHoverParentClassName()}
          $isOver={isOver}
          $isOpen={isMultiViewSelected}
          onClick={onButtonClick}
        >
          {multiViewDropIds.map((key, index) => (
            <MultiViewButtonDropArea
              key={key}
              index={index + 1}
              frame={visibleFrames.find(
                (frame) => frame.frameId === multiViewDetails.selectedFrameIds[index],
              )}
              isOpen={isMultiViewSelected || isOver}
              onFrameDragOver={(frameId, overFrameId) => {
                // If we are over a frame in the button, do nothing.
                if (multiViewDetails.selectedFrameIds.includes(overFrameId)) {
                  return;
                }
                onFrameDragOver(frameId, overFrameId);
              }}
              onFrameDragStart={(frameId) => {
                onFrameDragStart(frameId);
                wasDroppedOnAreaRef.current = false;
              }}
              onFrameDragEnd={(frameId) => {
                if (!frameIdOverMultiViewButton && !wasDroppedOnAreaRef.current && !isOver) {
                  // Remove the frame as we are not over the button or a drop area
                  updateSidepanelMultiViewSelectedFrameIds(
                    computeSelectedFrameIds(multiViewDetails.selectedFrameIds, frameId, index, true),
                  );
                }
                onFrameDragEnd();
              }}
              onDrop={(frameId) => {
                wasDroppedOnAreaRef.current = true;

                // If the button is not currently selected, open the button.
                if (!isMultiViewSelected) {
                  updateSelectedSideFrameId(FrameTypesEnum.MULTI_VIEW, props.panelId);
                }

                // Update the selected frame ids.
                updateSidepanelMultiViewSelectedFrameIds(
                  computeSelectedFrameIds(multiViewDetails.selectedFrameIds, frameId, index),
                );

                setFrameIdOverMultiViewButton(undefined);
              }}
            />
          ))}
        </StyledMultiViewButtonWrapperDiv>
      </TooltipCoordinator>
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function computeSelectedFrameIds(
  selectedFrameIds: ReadonlyArray<string | null>,
  frameId: string,
  index: number,
  shouldRemove?: boolean,
) {
  // If the frame is already selected, we should swap positions
  if (selectedFrameIds.includes(frameId) && !shouldRemove) {
    return swap(selectedFrameIds, index, selectedFrameIds.indexOf(frameId));
  }
  return _([...multiViewDropIds])
    .map((_value, arrayIndex) => {
      if (arrayIndex === index) {
        return shouldRemove ? null : frameId;
      }
      return selectedFrameIds[arrayIndex];
    })
    .value();
}
